import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged,
  updateProfile as fbUpdateProfile, updatePassword as fbUpdatePassword,
  deleteUser as fbDeleteUser, reauthenticateWithCredential, EmailAuthProvider,
  GoogleAuthProvider, signInWithPopup,
} from "firebase/auth";
import { auth } from "../lib/firebase";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  // null = still checking Firebase session, false = not signed in, object = signed in
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email.split("@")[0],
          photoURL: firebaseUser.photoURL || null,
        });
      } else {
        setUser(false);
      }
    });
    return unsub;
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return {
      uid: result.user.uid,
      email: result.user.email,
      name: result.user.displayName || email.split("@")[0],
    };
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const register = useCallback(async (name, email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (name) await fbUpdateProfile(result.user, { displayName: name });
    return {
      uid: result.user.uid,
      email: result.user.email,
      name: name || result.user.email.split("@")[0],
    };
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return {
      uid: result.user.uid,
      email: result.user.email,
      name: result.user.displayName || result.user.email.split("@")[0],
    };
  }, []);

  const updateDisplayName = useCallback(async (name) => {
    await fbUpdateProfile(auth.currentUser, { displayName: name });
    setUser((u) => ({ ...u, name }));
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, cred);
    await fbUpdatePassword(auth.currentUser, newPassword);
  }, []);

  const deleteAccount = useCallback(async (currentPassword) => {
    const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, cred);
    await fbDeleteUser(auth.currentUser);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, register, logout, updateDisplayName, changePassword, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
}
